import { addCartByBuyTogether, calculateSkuPrice } from '@/apis/dp/service';
import DividerAdd from '@/assets/icons/buy-together-divider.svg';
import CheckboxChecked from '@/assets/icons/dp-checkbox-checked.svg';
import CheckboxDefault from '@/assets/icons/dp-checkbox-default.svg';
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price/index';
import { dataExpTagConfig } from '@/components/product/const';
import SafeLink from '@/components/SafeLink';
import UKToast from '@/components/UKToast';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25217';
import { formatProductLink } from '@/utils/formatProductLink';
import { getUserUUIDFromCookie } from '@/utils/userUtils';
import { Button } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { observer } from 'mobx-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import { type ContentStruct, type ItemContentProps } from './type';
import UKLoadingIcon, { CIRCLE_COLOR_MAP, SIZE_MAP } from '@/components/UKLoadingIcon';
import { addCartUtil } from '@/utils/cartUtils';
import UKCheckbox from '@/components/UKCheckbox';

const StyledButton = muiStyled(Button)(() => ({
  minHeight: '40px',
  minWidth: '108px',
  backgroundColor: '#fff',
  borderColor: '#CC0C1C',
  color: '#CC0C1C',
  borderRadius: '4px',
  fontWeight: 'bold',
  lineHeight: '36px',
  padding: '0 16px',
  '&:hover': {
    backgroundColor: '#fcf1f2',
    // borderColor: '#CC0C1C',
  },
  '&:active': {
    backgroundColor: '#fae7e9',
  },
}));

const ProductCard = ({
  imageUrl,
  realPrice,
  imgStyle,
  isChecked = true,
  showCheckbox = true,
  wareName,
  skuId,
  commonTrackParams,
  localInfo,
  onCheckboxChange = () => {},
  loading,
}: Omit<
  ItemContentProps & {
    showCheckbox?: boolean;
    onCheckboxChange?: (checked: boolean) => void;
    isChecked?: boolean;
    isMainSku?: boolean;
    skuId?: string;
    wareName?: string;
    localInfo?: any;
    imgStyle?: {
      width: number;
      height: number;
    };
    commonTrackParams: any;
    loading?: boolean;
  },
  'isChoose' | 'isMainSku'
>) => {
  // const [checked, setChecked] = React.useState(isChecked);
  const renderChild = () => {
    return (
      <>
        <div className={styles.productCardImgWrapper}>
          <CDNImage src={imageUrl} width={imgStyle?.width || 90} height={imgStyle?.height || 90} />
        </div>
        {wareName && <div className={styles.displaySkuName}>{wareName}</div>}
        <div className={styles.productCardPriceWrapper}>
          <JDIPrice
            locale={localInfo?.languageCode}
            currency={localInfo?.currency}
            price={realPrice}
            symbolStyle={{ fontSize: '16px' }}
            integerStyle={{ fontSize: '20px' }}
            decimalPlaceStyle={{ fontSize: '16px' }}
          />
        </div>
      </>
    );
  };
  return (
    <div className={styles.productCard}>
      {showCheckbox && (
        // <Checkbox
        //   className={styles.dpBuyCheckbox}
        //   checked={isChecked}
        //   onChange={() => onCheckboxChange(!isChecked)}
        //   icon={<CheckboxDefault />}
        //   checkedIcon={<CheckboxChecked />}
        //   sx={{
        //     color: '#E0E1E5',
        //     '&.Mui-checked': {
        //       color: '#1A1A1A',
        //     },
        //   }}
        // />
        <UKCheckbox
          checkboxClassName={styles.dpBuyCheckbox}
          loadingBoxClassName={styles.buyTogetherLoadingBox}
          checked={isChecked}
          onClick={() => onCheckboxChange(!isChecked)}
          sx={{
            color: '#E0E1E5',
            marginLeft: 0,
            // '&.Mui-checked': {
            //   color: '#1A1A1A',
            // },
          }}
          loading={loading}
        />
      )}
      {showCheckbox ? (
        <SafeLink
          href={formatProductLink(skuId)}
          trackClickParams={{
            event_id: 'Productdetail_FreqBought_Match_Click',
            json_param: {
              ...commonTrackParams,
            },
          }}
        >
          {renderChild()}
        </SafeLink>
      ) : (
        <>{renderChild()}</>
      )}
    </div>
  );
};
export default observer(function BuyProductList({
  data,
  localInfo,
  commonTrackParams,
  onHandleAddCart,
}: {
  data: ContentStruct & {
    index?: number;
    i18n: {
      buttonText: string;
      totalPriceText: string;
    };
  };
  localInfo: any;
  commonTrackParams: any;
  onHandleAddCart?: () => void;
}) {
  const { t } = useTranslation();
  const cartUuidRef = useRef<string>('');
  //loading
  const [boughtToLoading, setBoughtToLoading] = useState<boolean>(false);
  const [checkLoadingStatus, setCheckLoadingStatus] = useState<Record<string, boolean>>({});

  // const [productData, setProductData] = useState<ContentStruct>(data);
  const currentOptId = data?.content.map((item) => item.skuId).join('_') + `_${data?.index || 0}`;
  const {
    cartNumStore,
    dpStore: { changeBuyTogetherState, buyTogetherState },
  } = useStores();
  const initSelectedSkuIds = useMemo<string[]>(() => {
    return data.content?.filter((item) => item.isChoose).map((item) => item.skuId) || [];
  }, [data]);

  const onCheckboxChanged = useCallback(
    async (checked: boolean, index: number, skuId: string, price: string) => {
      const { selectedSkuIds: cacheSelectedSkuIds = initSelectedSkuIds as string[] } =
        buyTogetherState.get(currentOptId) || {};
      const selectedSkuIds =
        data?.content
          ?.filter((item) => {
            if (skuId === item.skuId) {
              return checked;
            }
            return cacheSelectedSkuIds.includes(item.skuId);
          })
          .map((item) => item.skuId) || [];
      setCheckLoadingStatus({ ...checkLoadingStatus, [skuId]: true });
      const res = await calculateSkuPrice({
        skuIds: selectedSkuIds,
      });
      setCheckLoadingStatus({ ...checkLoadingStatus, [skuId]: false });

      if (res?.totalPrice) {
        changeBuyTogetherState(currentOptId, selectedSkuIds, res.totalPrice);
      }
      sendClickTrack.Productdetail_FreqBought_Select_Click({
        ...commonTrackParams,
        index: index - 1,
        skuid: skuId,
        price,
      });
    },
    [data],
  );

  const goToBuy = async () => {
    setBoughtToLoading(true);
    const { selectedSkuIds: cacheSelectedSkuIds = initSelectedSkuIds } =
      buyTogetherState.get(currentOptId) || {};
    if (!cacheSelectedSkuIds?.length) {
      return;
    }
    const products = cacheSelectedSkuIds.map((item) => {
      return {
        num: 1,
        skuId: item,
      };
    });
    const res = await addCartUtil(products);

    if (res?.result?.code === '200') {
      cartNumStore.updateCartNum(res?.result?.data);
      onHandleAddCart?.();
    }

    // const res = await addCartByBuyTogether({
    //   cartuuid: cartUuidRef.current,
    //   products: cacheSelectedSkuIds.map((item) => {
    //     return {
    //       num: 1,
    //       skuId: item,
    //       itemType: 1,
    //     };
    //   }),
    // });
    // if (res?.code === '200') {
    //   UKToast.show({
    //     content: t('dp.successCart'), // 弹窗内容
    //     icon: 'success', // 可选：success, fail, loading, warn, info，影响图标
    //   });
    //   window?.JDIheader?.initCartNum();
    //   onHandleAddCart?.();
    // } else {
    //   UKToast.show({
    //     content: t('dp.failCart'), // 弹窗内容
    //     icon: 'error', // 可选：success, fail, loading, warn, info，影响图标
    //   });
    // }
    sendClickTrack?.Productdetail_FreqBought_Addtocart_Click({
      ...commonTrackParams,
      skuid: cacheSelectedSkuIds,
      totalAmount: buyTogetherState.get(currentOptId)?.totalPrice || data?.totalPrice,
    });
    setBoughtToLoading(false);
  };

  useEffect(() => {
    cartUuidRef.current = getUserUUIDFromCookie();
  }, []);

  return (
    <div className={styles.buyBlock}>
      <div
        className={styles.productCardList}
        exptag={'exp|' + EXPOSURE_RECORD.Productdetail_FreqBought_Match_Expo}
        data-exptag-config={dataExpTagConfig}
        data-exptag-json-param={JSON.stringify({
          ...commonTrackParams,
          ...data?.content.reduce(
            (prev: any, next: ItemContentProps, index: number) => {
              return next.isMainSku
                ? prev
                : {
                    ...prev,
                    skuid: [...prev.skuid, next.skuId],
                    price: [...prev.price, next.realPrice],
                    // index: [...prev.index, index - 1]
                  };
            },
            {
              skuid: [],
              price: [],
              // index: []
            },
          ),
        })}
      >
        {data?.content.map((item: ItemContentProps, index: number) => {
          const cacheSelectedSkuIds = buyTogetherState.get(currentOptId)?.selectedSkuIds;
          const isChoose = cacheSelectedSkuIds?.includes(item.skuId) ?? item.isChoose;
          return (
            <Fragment key={`buy-product-${index}-${item.skuId}`}>
              <ProductCard
                commonTrackParams={{
                  ...commonTrackParams,
                  price: item.realPrice,
                  skuid: item.skuId,
                  index: index - 1,
                }}
                imageUrl={item.imageUrl}
                realPrice={item.realPrice}
                showCheckbox={!item?.isMainSku}
                isChecked={isChoose}
                wareName={item.wareName}
                skuId={item.skuId}
                onCheckboxChange={(checked: boolean) => {
                  onCheckboxChanged(checked, index, item.skuId, item.realPrice);
                }}
                localInfo={localInfo}
                loading={checkLoadingStatus?.[item?.skuId]}
              />
              {index !== data.content.length - 1 && (
                <div className={styles.productCardDivider}>
                  <DividerAdd />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <div className={styles.bottomAddCartBar}>
        <div
          className={styles.total}
          exptag={'exp|' + EXPOSURE_RECORD.Productdetail_FreqBought_Total_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify({
            ...commonTrackParams,
            totalAmount: buyTogetherState.get(currentOptId)?.totalPrice || data?.totalPrice,
          })}
        >
          <span className={styles.label}>{data?.i18n?.totalPriceText}:</span>
          <br />
          <JDIPrice
            locale={localInfo?.languageCode}
            currency={localInfo?.currency}
            price={buyTogetherState.get(currentOptId)?.totalPrice || data?.totalPrice}
            symbolStyle={{ fontSize: '16px' }}
            integerStyle={{ fontSize: '24px' }}
            decimalPlaceStyle={{ fontSize: '16px' }}
          />
        </div>

        <StyledButton
          className={styles.addCartBtn}
          exptag={'exp|' + EXPOSURE_RECORD.Productdetail_FreqBought_Addtocart_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify({
            ...commonTrackParams,
            skuid: data?.content.filter((item) => !item.isMainSku).map((item) => item.skuId),
            totalAmount: buyTogetherState.get(currentOptId)?.totalPrice || data?.totalPrice,
          })}
          variant='outlined'
          onClick={goToBuy}
          disableRipple
          startIcon={
            boughtToLoading ? (
              <UKLoadingIcon type={CIRCLE_COLOR_MAP.RED} size={SIZE_MAP.SIZE_20} />
            ) : null
          }
        >
          {boughtToLoading ? '' : data?.i18n?.buttonText}
        </StyledButton>
      </div>
    </div>
  );
});
